import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Button, List, Modal, Typography } from '@robinpowered/ui-kit';
import { ExternalAccountType } from 'generated/graphql';
import type {
  CalendarHealth,
  IntegrationNeedingAttention,
} from 'hooks/useCalendarIntegrationHealth';

const { Text } = Typography;

// How each provider is named. One entry per provider, keyed off the enum
// rather than a lowercased string, so adding a new `ExternalAccountType`
// fails to compile here rather than silently rendering with no name.
const PROVIDER_NAME_KEYS = {
  [ExternalAccountType.Google]: 'provider.google',
  [ExternalAccountType.MicrosoftGraph]: 'provider.microsoft_graph',
} as const satisfies Record<ExternalAccountType, string>;

export type ReconnectAccountsModalProps = {
  open: boolean;
  onClose: () => void;
  health: CalendarHealth;
  /** True while the "unavailable" state's own retry probe is in flight. */
  refetching: boolean;
  onRetry: () => void;
  /** The provider whose OAuth popup is currently in flight, so only that row's button shows a spinner. */
  pendingType: ExternalAccountType | null;
  /** Providers whose most recent OAuth attempt failed; those rows show an inline error until the user tries again or the row clears on its own. */
  failedTypes: ReadonlySet<ExternalAccountType>;
  /** Runs the in-place OAuth reconnect for one row. Called straight from the row's onClick — see `useOAuthReconnect`. */
  onReconnect: (type: ExternalAccountType) => void;
};

/**
 * The shell-hosted "Reconnect My Accounts" modal. Lets a user see which
 * calendar integrations need attention and reconnect them without leaving
 * the page.
 *
 * Purely presentational: every value it renders and every action it can
 * trigger comes in through props. `ReconnectAccountsHost` owns the health
 * probe, the URL open/close state, and the OAuth handshake itself.
 */
export const ReconnectAccountsModal = ({
  open,
  onClose,
  health,
  refetching,
  onRetry,
  pendingType,
  failedTypes,
  onReconnect,
}: ReconnectAccountsModalProps): JSX.Element => {
  const { t } = useTranslation('reconnectAccounts');

  // The one affordance a row is allowed to have. Reconnecting is offered for
  // anything the user can act on — including a transient probe failure,
  // where re-authorizing is harmless and beats asking them to guess whether
  // to wait. `contact_support` gets nothing: there is no self-service fix.
  const renderRowCta = (
    integration: IntegrationNeedingAttention,
    providerName: string
  ): JSX.Element | null => {
    switch (integration.resolution) {
      case 'reauthorize':
      case 'retry':
        return (
          <Button
            type="primary"
            loading={pendingType === integration.type}
            // One handshake at a time. Each provider opens its own popup, and
            // letting a second start while one is in flight means two consent
            // screens competing for focus and two pollers racing. The row stays
            // visible and keeps its button — only the ability to start a second
            // attempt is withheld.
            disabled={pendingType !== null && pendingType !== integration.type}
            // Every row's button reads the same, so the accessible name has
            // to carry which provider it belongs to. Interpolated through a
            // single key so translators control word order and spacing.
            aria-label={t('cta.reconnect_provider', { provider: providerName })}
            onClick={() => onReconnect(integration.type)}
          >
            {t('cta.reconnect')}
          </Button>
        );
      case 'contact_support':
        return null;
    }
  };

  const renderBody = (): JSX.Element => {
    if (health.state === 'loading') {
      return <StateMessage>{t('loading')}</StateMessage>;
    }

    // We could not check at all. Say exactly that — this state knows nothing
    // about any specific connection — and leave the user a way forward,
    // since there are no rows here to carry a retry button.
    if (health.state === 'unavailable') {
      return (
        <>
          <StateMessage>{t('unavailable')}</StateMessage>
          <Button loading={refetching} onClick={onRetry}>
            {t('cta.retry')}
          </Button>
        </>
      );
    }

    if (health.needsAttention.length === 0) {
      // `uncertain` means we hid at least one provider on a guess, so we
      // can't honestly claim everything is working.
      return (
        <StateMessage>
          {health.uncertain ? t('incomplete') : t('empty')}
        </StateMessage>
      );
    }

    return (
      <>
        <List<IntegrationNeedingAttention>
          dataSource={health.needsAttention}
          renderItem={(integration) => {
            const providerName = t(PROVIDER_NAME_KEYS[integration.type]);
            const cta = renderRowCta(integration, providerName);
            return (
              <List.Item
                key={integration.type}
                actions={cta ? [cta] : undefined}
              >
                <List.Item.Meta
                  title={providerName}
                  description={
                    <>
                      {integration.errorMessage && (
                        <div>{integration.errorMessage}</div>
                      )}
                      {/* The server's next step. Carries a contact_support
                          row, which has no button of its own. */}
                      {integration.guidance && (
                        <div>{integration.guidance}</div>
                      )}
                      {/* A contact_support row has no CTA, and the server can
                          report one unhealthy but with no message or guidance
                          at all. Without this it would render just the provider
                          name — blank and unactionable — while the summary above
                          says the calendar is broken. Give it a way forward. */}
                      {integration.resolution === 'contact_support' &&
                        !integration.errorMessage &&
                        !integration.guidance && (
                          <div>{t('contact_support')}</div>
                        )}
                      {/* The row's own reconnect attempt just failed — stays
                          on the row rather than closing the modal or showing
                          a global error, so the user can simply try again. */}
                      {failedTypes.has(integration.type) && (
                        <ErrorText>{t('reconnect_error')}</ErrorText>
                      )}
                    </>
                  }
                />
              </List.Item>
            );
          }}
        />
        {/* A provider was hidden on a guess, so this list may be incomplete —
            say so rather than letting the shown rows imply completeness. */}
        {health.uncertain && <StateMessage>{t('incomplete')}</StateMessage>}
      </>
    );
  };

  // One sentence naming exactly which personal calendars are broken and what
  // stops working until they're reconnected. Only the providers actually
  // needing attention are named, so it always matches the rows below.
  const summaryText = (): string => {
    const brokenTypes = new Set(
      health.state === 'ready'
        ? health.needsAttention.map((integration) => integration.type)
        : []
    );
    const google = brokenTypes.has(ExternalAccountType.Google);
    const microsoft = brokenTypes.has(ExternalAccountType.MicrosoftGraph);
    if (google && microsoft) {
      return t('summary.both');
    }
    if (microsoft) {
      return t('summary.microsoft');
    }
    return t('summary.google');
  };

  // The summary tells the user to reconnect things, so it only belongs above
  // an actual list. Showing it over "all of your connections are working" —
  // which is reachable straight from an error banner — puts two
  // contradicting sentences one line apart.
  const hasRows = health.state === 'ready' && health.needsAttention.length > 0;

  return (
    <Modal open={open} onCancel={onClose} footer={null} title={t('title')}>
      {hasRows && <Description>{summaryText()}</Description>}
      {renderBody()}
    </Modal>
  );
};

const Description = styled(Text)`
  display: block;
  color: var(--Colors-Neutral-Text-colorTextSecondary, rgba(0, 0, 0, 0.65));
  margin-bottom: var(--Space-Margin-marginSM, 12px);
`;

const StateMessage = styled(Text)`
  display: block;
  color: var(--Colors-Neutral-Text-colorTextSecondary, rgba(0, 0, 0, 0.65));
`;

const ErrorText = styled(Text)`
  display: block;
  color: var(--ant-color-error, #ff4d4f);
`;
